import type { FunctionComponent } from "react";
import { useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { IconButton } from "@similarweb/ui-components";

import { showSuccessToast } from "app/actions/toast_actions";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { keywordGroupsQueryClient, KEYWORD_GROUP_PREFIX } from "app/services/keyword-groups";
import { EmptyKeywordsQueryBar } from "app/components/compare/KeywordsQueryBar/EmptyKeywordsQueryBar";
import { KeywordsQueryBar } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBar";
import { adaptKeywordsQueryBarProps } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarHelper";
import type { QueryBarItemMode } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarTypes";
import { EducationContainer } from "app/components/educationbar/educationContainer";
import educationHook from "app/components/educationbar/educationHook";
import PageTitle from "app/components/React/PageTitle/PageTitle";
import { SubNav } from "app/components/sub-navigation-bar/SubNav";
import useLocalStorage from "app/custom-hooks/useLocalStorage";
import { translate } from "app/filters/i18nFilter";
import { KeywordAnalysisFilters } from "./KeywordAnalysisFilters";
import { useKeywordAnalysisNavigationTargets } from "./keywordAnalysisNavigationTargets";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { BasicDurations } from "app/services/DurationService";
import { ProductTours, ProductToursLocalStorageKeys, showIntercomTour } from "app/services/IntercomProductTourService";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import { allTrackers } from "app/services/track/track";

const IconButtonStyled = styled(IconButton)`
    margin-right: 12px;
`;

const ItemContainer = styled(FlexRow)`
    margin-left: 16px;
`;

interface IKeywordAnalysisSubNavProps {
    categories: any[];
    customCategories: any[];
    selectedCat: string;
    trackName: string;
    trackContext: string;
    placeholderItem: object;
    removeItemTooltip: object;
    mode: QueryBarItemMode;
    hasKeywordsGroupsEnabled?: boolean;
    searchTerm?: string;
    showAutoCreateListCTA?: boolean;
    showCompactCreateListCTAButton?: boolean;
    isLoading?: boolean;
    showCompareButton?: boolean;
    topLeftExtraComponent?: React.ReactNode;
    hideKeywordGroups?: boolean;
}

interface IRouterKWState extends IRouterState {
    onSearchItemClickCustomStateId?: string;
}

interface StateAndParams {
    params: {
        keyword: string;
        country: string;
    };
    state: IRouterState;
}

export const KeywordAnalysisSubNav: FunctionComponent<IKeywordAnalysisSubNavProps> = (props) => {
    const { params, state }: StateAndParams = useCurrentStateAndParams();
    const { isPersistent } = useSelector((state) => state.QueryBarReducer);
    const [showEducationIcon, showEducationBar, setValues] = educationHook();
    const dispatch = useDispatch();
    const navTargets = useKeywordAnalysisNavigationTargets();

    const {
        isLoading,
        showAutoCreateListCTA = true,
        showCompactCreateListCTAButton = true,
        showCompareButton = false,
    } = props;
    const homeState = proNavigator.current()?.homeState;
    const isKeywordGroup = params.keyword?.startsWith(KEYWORD_GROUP_PREFIX);
    const shouldConfirmDeleteKeywordList = state.name === KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE;

    const queryBarProps = adaptKeywordsQueryBarProps(props);
    queryBarProps.showKeywordGroups = !props.hideKeywordGroups;
    queryBarProps.onSearchItemClick = (item) => {
        const currentPageState = proNavigator.current() as IRouterKWState;
        const customStateId = currentPageState?.customData?.onSearchItemClickCustomStateId;
        const currPageDefaultParams = currentPageState.resetOnKeywordChange && currentPageState.reset;

        const keyword = typeof item === "string" ? item : item.Id ? KEYWORD_GROUP_PREFIX + item.Id : item.name;
        proNavigator.go(
            customStateId ?? currentPageState,
            {
                ...currPageDefaultParams,
                keyword: keyword,
            },
            { reload: true },
        );
    };

    /**
     * Resolves which product tour should be shown for the keyword query bar.
     * this depends on the page the user is located on.
     */
    const resolveProductTourIds = () => {
        return {
            tourId: ProductTours.KeywordAnalysisQueryBar,
            storageId: ProductToursLocalStorageKeys.KeywordAnalysisQueryBar,
        };
    };

    const { tourId, storageId } = resolveProductTourIds();
    const [hasViewedProductTour, setHasViewedProductTour] = useLocalStorage(storageId);

    const onCloseEducationBar = () => {
        setValues(true, false);
    };
    const onEducationIconClick = () => {
        setValues(false, true);
    };

    useEffect(() => {
        // Sorry for comparing a string to bool value, useLocalStorage is funky :(
        // In case the user is seeing the keywords query bar for the first time (in each available package)
        // show him a product tour
        if (!isLoading && hasViewedProductTour !== "true") {
            showIntercomTour(tourId);
            setHasViewedProductTour("true");
        }
    }, [isLoading, hasViewedProductTour, setHasViewedProductTour, tourId]);

    const reloadWithSavedList = (savedList: IUserKeywordGroup) => {
        proNavigator.go(
            proNavigator.current().name,
            {
                keyword: `*${savedList.Id}`,
            },
            { reload: true },
        );
    };

    const showToast = (newlyCreatedList) => {
        reloadWithSavedList(newlyCreatedList);
        dispatch(showSuccessToast(translate("create.list.wizard.toast", { listName: newlyCreatedList.Name })));
    };

    const onNavigateToMonitorKeywordsPage = async () => {
        const selectedKeywordGroup = await keywordGroupsQueryClient.getGroupById(
            UserCustomCategoryService.removeCategoryIdPrefix(params.keyword),
        );
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `view list in my keyword lists/${selectedKeywordGroup.Name || ""}`,
        );
        proNavigator.go(
            navTargets.monitorKeywordsState,
            {
                keywordGroupId: params.keyword.substring(1), // remove the * from the keyword
                country: params.country,
                duration: BasicDurations.LAST_TWELVE_MONTHS,
                webSource: devicesTypes.DESKTOP,
                keyword: params.keyword,
            },
            { reload: true },
        );
    };

    const BottomRightComponent = state.bottomRightComponent
        ? (state.bottomRightComponent as React.ElementType)
        : KeywordAnalysisFilters;

    return isPersistent ? (
        <SubNav
            topLeftComponent={
                <EmptyKeywordsQueryBar
                    showKeywordGroups={queryBarProps.showKeywordGroups}
                    mode={queryBarProps.mode}
                    isLoading={queryBarProps.isLoading}
                    showAutoCreateListCTA={showAutoCreateListCTA}
                    showGoToMonitorKeywordListButton
                    shouldConfirmDeleteKeywordList={shouldConfirmDeleteKeywordList}
                />
            }
            bottomLeftComponent={<PageTitle />}
        />
    ) : params.keyword?.length ? (
        <SubNav
            topLeftComponent={
                <ItemContainer>
                    <KeywordsQueryBar
                        {...queryBarProps}
                        showSearch={false}
                        showAutoCreateListCTA={showAutoCreateListCTA}
                        showGoToMonitorKeywordListButton
                        shouldConfirmDeleteKeywordList={shouldConfirmDeleteKeywordList}
                        showCompareButton={showCompareButton}
                        onSaveKeywordList={showToast}
                        onEditKeywordList={reloadWithSavedList}
                        showCompactCreateListCTAButton={showCompactCreateListCTAButton}
                    />
                    {props.topLeftExtraComponent}
                </ItemContainer>
            }
            {...(isKeywordGroup && {
                topRightComponent: (
                    <IconButtonStyled
                        type="outlined"
                        iconName="arrow-right"
                        onClick={onNavigateToMonitorKeywordsPage}
                    >
                        {translate("keyword.analysis.subnav.monitor.keywords.button.text")}
                    </IconButtonStyled>
                ),
            })}
            bottomLeftComponent={
                <PageTitle
                    showEducationIcon={showEducationIcon}
                    onEducationIconClick={onEducationIconClick}
                />
            }
            bottomRightComponent={<BottomRightComponent />}
            education={showEducationBar ? <EducationContainer onCloseEducationComponent={onCloseEducationBar} /> : null}
        />
    ) : null;
};
